import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Button, CardMedia, IconButton, Input } from "@mui/material";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import useAuth from "../../hooks/useAuth";
import { UploadMedia } from "../../utils/mediaUpload";
import { isValidEmail, isValidInput } from "../../utils/validations";
import MainContainer from "../../layout/MainContainer";
import { showError, showToast } from "../../constants/toasts";
import { usePutUpdateProfileMutation } from "../../services/auth";
import useTranslation from "../../hooks/traslation";

const EditProfile = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const translation = useTranslation();

  const [updateProfile] = usePutUpdateProfileMutation();

  const [profileImage, setProfileImage] = useState("");
  const [fullName, setFullName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phoneNo || "");
  const [dialCode, setDialCode] = useState(user?.dialCode || "");
  const [error, setError] = useState(false);

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ""));
    setDialCode(country?.dialCode);
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }
    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      showError("Please upload the image in the JPG, JPEG or PNG format");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setProfileImage(imageUrl?.data?.image || "");
    }
  };

  const onSubmit = async () => {
    setError(true);
    if (!phone || !fullName) {
      return;
    }
    const body = {
      phoneNo: phone,
      // email,
      name: fullName,
      image: profileImage,
      dialCode: (dialCode.includes("+") ? "" : "+") + dialCode,
    };
    try {
      const response = await updateProfile(body).unwrap();
      if (response?.statusCode === 200) {
        showToast("Profile updated successfully");
        navigate("/dashboard");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (user) {
      setFullName(user?.name || "");
      setEmail(user?.email || "");
      setPhone(user?.phoneNo || "");
      setProfileImage(user?.image || "");
      setDialCode(user?.dialCode || "");
    }
  }, [user]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-profile")}>
            Manage Profile
          </span>{" "}
          - Edit Profile
        </p>
      </div>

      <div className="cards">
        <form className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>

              {profileImage ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={profileImage}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon onClick={() => setProfileImage("")} />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg,image/jpg",
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = null;
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label>Full Name</label>
              <TextField
                variant="outlined"
                placeholder="Full Name"
                fullWidth
                hiddenLabel
                value={fullName}
                onChange={(val) => {
                  if (isValidInput(val.target.value)) {
                    setFullName(val.target.value);
                  }
                }}
                inputProps={{ maxLength: 40 }}
              />
              {!fullName && error ? (
                <h6 className="err_msg">This field is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_33">
              <label>E-mail</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                // disabled={user?.email ? true : false}
                hiddenLabel
                value={email}
                onChange={(val) => {
                  const input = val.target.value;
                  setEmail(val.target.value);
                  setError(!isValidEmail(val.target.value)); // set error to true if email is invalid
                }}
                // onChange={(val) => {
                //   if (isValidInput(val.target.value)) {
                //     setEmail(val.target.value);
                //   }
                // }}
              />
              {/* {!email && error ? (
                <h6 className="err_msg">This field is required</h6>
              ) : (
                ""
              )} */}

              {!email && error ? (
                <h6 className="err_msg">This field is required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={dialCode + phone}
                country={"uae"}
                inputClass="phoneInput"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {!phone && error ? (
                <h6 className="err_msg">This field is required</h6>
              ) : (
                ""
              )}
              {phone.length >= 1 && phone.length < 5 && error ? (
                <h6 className="err_msg">Please enter a valid phone number</h6>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" onClick={onSubmit}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default EditProfile;
